/**
 * Unified Help Speed Dial Component
 * 
 * Enterprise-grade speed dial system providing contextual quick actions based on
 * user authentication state and role. Includes real-time badge counts for chat,
 * messages, and notifications. Shows different actions for guests, authenticated users,
 * and role-specific actions for admin, coordinator, training_manager, supervisor, and attendee.
 * 
 * @author Andres Nava
 * @version 2.0.0
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
  Box,
  Typography,
  Badge,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as TourIcon,
  Message as ChatIcon,
  BugReport as BugReportIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Inbox as InboxIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  Favorite as FavoriteIcon,
  ContactSupport as ContactSupportIcon,
  Help as HelpIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Settings as SettingsIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  ManageAccounts as ManageAccountsIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/authContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { filterPlatformTours } from '../../../shared/onboarding/tourCatalog';
import { OnboardingMenu } from '../onboarding/OnboardingMenu';
import { TOUR_DOM_EVENTS } from '../onboarding/onboardingDomEvents';
import { logger } from '../../utils/logger';
import { useUnreadCounts } from '../../hooks/useUnreadCounts';
import { getPrimaryRole, hasRole, hasAnyRole } from '../../utils/roleHelpers';
import { navigateToMainSitePath, getSubdomainUrl, SUBDOMAINS, detectSubdomain } from '../../utils/subdomainUtils';
import { shouldHideUnifiedSpeedDial } from '../../utils/speedDialVisibility';
import { fabBottomCss, fabRightCss } from '../../constants/fabLayout';
import { useMuiSpeedDialAccessibleActionsRole } from '../../hooks/useMuiSpeedDialAccessibleActionsRole';
import { toBackofficeRoutePath } from '../../pages/backoffice/backofficeRoutePaths';
import AdBlockingDiagnostics from '../debug/AdBlockingDiagnostics';
import { useAdminAvailability } from '../../hooks/useAdminAvailability';
import { LiveAgentAvailabilityCallout } from './LiveAgentAvailabilityCallout';

interface UnifiedHelpSpeedDialProps {
  /** Handler for opening bug report dialog */
  onBugReport?: () => void;
  /** Whether to show bug report option */
  showBugReport?: boolean;
  /** Whether to show chat option */
  showChat?: boolean;
  /** Whether to show onboarding tours option */
  showTours?: boolean;
  /** Custom aria label */
  ariaLabel?: string;
}

export const UnifiedHelpSpeedDial: React.FC<UnifiedHelpSpeedDialProps> = ({
  onBugReport,
  showBugReport = true,
  showChat = true,
  showTours = true,
  ariaLabel = 'Help & Support',
}) => {
  if (import.meta.env.DEV) {
    logger.debug('UnifiedHelpSpeedDial rendered', { showTours, showChat, showBugReport, location: window.location.pathname });
  }
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tourMenuAnchorEl, setTourMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showAdBlockingDiagnostics, setShowAdBlockingDiagnostics] = useState(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const speedDialButtonRef = useRef<HTMLButtonElement | null>(null);
  const speedDialRootRef = useRef<HTMLDivElement | null>(null);

  useMuiSpeedDialAccessibleActionsRole(ariaLabel, open, speedDialRootRef);

  // Authentication context
  const { isAuthenticated, user, isLoading: authLoading, isInitializing: authInitializing } = useAuth();

  // Onboarding context - hooks must be called unconditionally
  const { state, getAvailableTours, getAllTours, isFlowCompleted, config: onboardingConfig } = useOnboarding();

  // Unread counts hook
  const { counts: unreadCounts, isLoading: countsLoading } = useUnreadCounts(
    isAuthenticated,
    user?.id,
    !authLoading && !authInitializing
  );

  // Subdomain detection - check if we're on a PWA page route or backoffice
  // PWA pages and backoffice pages are the exceptions - all other platform pages should show the speed dial
  const subdomainInfo = useMemo(() => detectSubdomain(), []);
  const location = useLocation();
  const isDashboardMobile = isMobile && location.pathname.startsWith('/dashboard');
  const fabSizePx = isDashboardMobile ? 48 : 56;
  const shouldHideSpeedDial = useMemo(
    () => shouldHideUnifiedSpeedDial(location.pathname, subdomainInfo.subdomain),
    [location.pathname, subdomainInfo.subdomain]
  );

  // Live agent presence for visitor callout + FAB pulse (independent of chat widget).
  const agentAvailability = useAdminAvailability({
    enabled: showChat && !shouldHideSpeedDial,
    checkInterval: 30000,
  });
  const isLiveAgentOnline = Boolean(showChat && agentAvailability.isOnline);

  // Role detection
  const primaryRole = useMemo(() => getPrimaryRole(user), [user]);
  const isAdmin = useMemo(() => hasRole(user, 'admin'), [user]);
  const isCoordinator = useMemo(() => hasRole(user, 'coordinator'), [user]);
  const isTrainingManager = useMemo(() => hasRole(user, 'training_manager'), [user]);
  const isSupervisor = useMemo(() => hasRole(user, 'supervisor'), [user]);
  const isDevMode = useMemo(() => import.meta.env.DEV === true, []);

  // Use getAllTours for display check (show tours for all users)
  // Use getAvailableTours for counting incomplete tours (user-specific)
  // Add defensive checks in case context isn't available
  const allTours = useMemo(() => {
    if (!getAllTours) {
      logger.warn('getAllTours not available in OnboardingContext');
      return [];
    }
    try {
      const tours = getAllTours();
      if (tours.length === 0) {
        logger.warn('getAllTours returned empty array - tours may not be loaded yet');
      } else if (import.meta.env.DEV) {
        logger.debug('getAllTours returned', { count: tours.length, tourIds: tours.map(t => t.id) });
      }
      return tours;
    } catch (error) {
      logger.error('Error calling getAllTours', error);
      return [];
    }
  }, [getAllTours]);
  
  const availableTours = useMemo(() => {
    if (!getAvailableTours) {
      return [];
    }
    try {
      return getAvailableTours();
    } catch (error) {
      logger.error('Error calling getAvailableTours', error);
      return [];
    }
  }, [getAvailableTours]);

  const platformTours = useMemo(
    () => filterPlatformTours(availableTours),
    [availableTours],
  );

  const completedTours = useMemo(() => {
    if (!isFlowCompleted) return [];
    return platformTours.filter((tour) => isFlowCompleted(tour.id));
  }, [platformTours, isFlowCompleted]);

  const incompleteTourCount = platformTours.length - completedTours.length;
  const hasIncompleteTours = incompleteTourCount > 0;

  const helpTourStepActive = useMemo(() => {
    if (!state.isRunning) return false;
    const step = state.currentSteps[state.currentStepIndex];
    return step?.stepName === 'welcome_help';
  }, [state.currentStepIndex, state.currentSteps, state.isRunning]);

  // Debug logging for tours button visibility
  useEffect(() => {
    if (showTours && import.meta.env.DEV) {
      logger.debug('Tours button visibility check', {
        showTours,
        allToursCount: allTours.length,
        availableToursCount: availableTours.length,
        willShow: showTours && allTours.length > 0,
        hasGetAllTours: !!getAllTours,
        hasGetAvailableTours: !!getAvailableTours,
      });
    }
  }, [showTours, allTours.length, availableTours.length, getAllTours, getAvailableTours]);

  const handleOpen = useCallback(() => {
    if (state.isRunning && !helpTourStepActive) {
      return;
    }
    // Store the previously focused element
    previousFocusRef.current = document.activeElement as HTMLElement;
    setOpen(true);
    if (import.meta.env.DEV) {
      logger.debug('Help speed dial opened');
    }
  }, [helpTourStepActive, state.isRunning]);

  const handleClose = useCallback((options?: { restoreFocus?: boolean }) => {
    setOpen(false);
    const restoreFocus = options?.restoreFocus !== false;
    if (!restoreFocus) {
      return;
    }
    // Restore focus to the previously focused element
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    } else if (speedDialButtonRef.current) {
      speedDialButtonRef.current.focus();
    }
    if (import.meta.env.DEV) {
      logger.debug('Help speed dial closed');
    }
  }, []);

  const closeForTour = useCallback(() => {
    setTourMenuAnchorEl(null);
    handleClose({ restoreFocus: false });
  }, [handleClose]);

  // Tour step "Help is always here" expands the dial; keep closed on all other tour steps.
  useEffect(() => {
    const onTourOpen = () => handleOpen();
    window.addEventListener(TOUR_DOM_EVENTS.OPEN_HELP_SPEED_DIAL, onTourOpen);
    window.addEventListener(TOUR_DOM_EVENTS.CLOSE_HELP_SPEED_DIAL, closeForTour);
    return () => {
      window.removeEventListener(TOUR_DOM_EVENTS.OPEN_HELP_SPEED_DIAL, onTourOpen);
      window.removeEventListener(TOUR_DOM_EVENTS.CLOSE_HELP_SPEED_DIAL, closeForTour);
    };
  }, [closeForTour, handleOpen]);

  useEffect(() => {
    if (state.isRunning && open && !helpTourStepActive) {
      closeForTour();
    }
  }, [closeForTour, helpTourStepActive, open, state.isRunning]);

  const handleChatClick = useCallback(() => {
    handleClose();
    // Dispatch custom event to open chat widget
    window.dispatchEvent(new Event('openChatWidget'));
    if (import.meta.env.DEV) {
      logger.debug('Chat support opened from help speed dial');
    }
  }, [handleClose]);

  const handleBugReportClick = useCallback(() => {
    handleClose();
    if (onBugReport) {
      onBugReport();
      if (import.meta.env.DEV) {
        logger.debug('Bug report opened from help speed dial');
      }
    } else {
      logger.warn('Bug report button clicked but onBugReport handler is not provided');
    }
  }, [onBugReport, handleClose]);

  const handleToursClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    // Don't close the speed dial - open the tours menu
    setTourMenuAnchorEl(event.currentTarget);
    if (import.meta.env.DEV) {
      logger.debug('Tours menu opened from help speed dial');
    }
  }, []);

  const handleTourMenuClose = () => {
    setTourMenuAnchorEl(null);
    handleClose();
  };

  // Navigation handlers
  const handleDashboardClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/dashboard');
    if (import.meta.env.DEV) {
      logger.debug('Dashboard navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleMessagesClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/messages');
    if (import.meta.env.DEV) {
      logger.debug('Messages navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleNotificationsClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/notifications');
    if (import.meta.env.DEV) {
      logger.debug('Notifications navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleFavoritesClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/favorites');
    if (import.meta.env.DEV) {
      logger.debug('Favorites navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleContactClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/contact');
    if (import.meta.env.DEV) {
      logger.debug('Contact navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleHelpClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/faq');
    if (import.meta.env.DEV) {
      logger.debug('Help/FAQ navigation from speed dial');
    }
  }, [handleClose, navigate]);

  // Role-specific navigation handlers
  const handleBackofficeClick = useCallback(() => {
    handleClose();
    window.location.href = getSubdomainUrl(SUBDOMAINS.BACKOFFICE) || '/backoffice';
    if (import.meta.env.DEV) {
      logger.debug('Backoffice navigation from speed dial');
    }
  }, [handleClose]);

  const handleSystemSettingsClick = useCallback(() => {
    handleClose();
    navigate(toBackofficeRoutePath('/backoffice')!);
    if (import.meta.env.DEV) {
      logger.debug('System Settings navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleCoordinatorEventCreateClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/coordinator-event-create');
    if (import.meta.env.DEV) {
      logger.debug('Coordinator Event Create navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleEventManagementClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/dashboard/event-management');
    if (import.meta.env.DEV) {
      logger.debug('Event Management navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleAgencyProfileClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/dashboard/agency-profile');
    if (import.meta.env.DEV) {
      logger.debug('Agency Profile navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleTrainingManagerEventCreateClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/training-manager-event-create');
    if (import.meta.env.DEV) {
      logger.debug('Training Manager Event Create navigation from speed dial');
    }
  }, [handleClose, navigate]);

  const handleSupervisorMembersClick = useCallback(() => {
    handleClose();
    navigateToMainSitePath(navigate, '/supervisor/members');
    if (import.meta.env.DEV) {
      logger.debug('Supervisor Members navigation from speed dial');
    }
  }, [handleClose, navigate]);

  // Ad Blocking Diagnostics handler (admin only, dev mode only)
  const handleAdBlockingDiagnosticsClick = useCallback(() => {
    if (isAdmin && isDevMode) {
      setShowAdBlockingDiagnostics(prev => !prev);
      if (import.meta.env.DEV) {
        logger.debug('Ad Blocking Diagnostics toggled', { show: !showAdBlockingDiagnostics });
      }
    }
  }, [isAdmin, isDevMode, showAdBlockingDiagnostics]);

  // Action type definition
  interface SpeedDialActionType {
    icon: React.ReactElement;
    name: string;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    color: string;
    badge?: number;
    badgeColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    highlight?: boolean;
    devOnly?: boolean;
    requiresRole?: string | string[];
    requiresAnyRole?: string[];
  }

  // Guest actions
  const getGuestActions = useCallback((): SpeedDialActionType[] => {
    const actions: SpeedDialActionType[] = [];

    if (showChat) {
      actions.push({
        icon: <ChatIcon />,
        name: isLiveAgentOnline ? 'Live Chat — agent online' : 'Chat Support',
        onClick: handleChatClick,
        color: isLiveAgentOnline ? theme.palette.success.main : theme.palette.primary.main,
        badge: unreadCounts.chat > 0 ? unreadCounts.chat : undefined,
        badgeColor: isLiveAgentOnline ? 'success' : 'primary',
        highlight: isLiveAgentOnline,
      });
    }

    // Always show tours button if showTours is true AND system-wide onboarding is enabled
    // If tours aren't loaded yet, still show the button so users can access tours menu
    if (showTours && onboardingConfig?.enabled !== false) {
      const tourCount = allTours.length;
      const hasTours = tourCount > 0;
      actions.push({
        icon: hasTours && hasIncompleteTours ? <TourIcon /> : hasTours ? <CheckCircleIcon /> : <TourIcon />,
        name: hasTours 
          ? (hasIncompleteTours ? `Guided Tours (${incompleteTourCount})` : 'Guided Tours ✓')
          : 'Guided Tours',
        onClick: handleToursClick,
        color: hasTours && hasIncompleteTours ? theme.palette.warning.main : hasTours ? theme.palette.success.main : theme.palette.info.main,
        badge: hasTours && hasIncompleteTours ? incompleteTourCount : undefined,
        highlight: hasTours && hasIncompleteTours,
      });
    }

    if (showBugReport) {
      actions.push({
        icon: <BugReportIcon />,
        name: 'Report Issue',
        onClick: handleBugReportClick,
        color: theme.palette.error.main,
      });
    }

    actions.push({
      icon: <ContactSupportIcon />,
      name: 'Contact Us',
      onClick: handleContactClick,
      color: theme.palette.info.main,
    });

    actions.push({
      icon: <HelpIcon />,
      name: 'Help/FAQ',
      onClick: handleHelpClick,
      color: theme.palette.info.main,
    });

    return actions;
  }, [showChat, showTours, showBugReport, allTours.length, hasIncompleteTours, incompleteTourCount, unreadCounts.chat, onboardingConfig?.enabled, handleChatClick, handleToursClick, handleBugReportClick, handleContactClick, handleHelpClick, theme, isLiveAgentOnline]);

  // Base authenticated actions (all logged-in users)
  const getBaseAuthenticatedActions = useCallback((): SpeedDialActionType[] => {
    const actions: SpeedDialActionType[] = [];

    if (showChat) {
      actions.push({
        icon: <ChatIcon />,
        name: isLiveAgentOnline ? 'Live Chat — agent online' : 'Chat Support',
        onClick: handleChatClick,
        color: isLiveAgentOnline ? theme.palette.success.main : theme.palette.primary.main,
        badge: unreadCounts.chat > 0 ? unreadCounts.chat : undefined,
        badgeColor: isLiveAgentOnline ? 'success' : 'primary',
        highlight: isLiveAgentOnline,
      });
    }

    actions.push({
      icon: <InboxIcon />,
      name: 'Messages',
      onClick: handleMessagesClick,
      color: theme.palette.primary.main,
      badge: unreadCounts.messages > 0 ? unreadCounts.messages : undefined,
      badgeColor: 'error',
    });

    actions.push({
      icon: <NotificationsIcon />,
      name: 'Notifications',
      onClick: handleNotificationsClick,
      color: theme.palette.primary.main,
      badge: unreadCounts.notifications > 0 ? unreadCounts.notifications : undefined,
      badgeColor: 'error',
    });

    actions.push({
      icon: <DashboardIcon />,
      name: 'Dashboard',
      onClick: handleDashboardClick,
      color: theme.palette.primary.main,
    });

    actions.push({
      icon: <FavoriteIcon />,
      name: 'My Favorites',
      onClick: handleFavoritesClick,
      color: theme.palette.primary.main,
    });

    // Always show tours button if showTours is true AND system-wide onboarding is enabled
    // If tours aren't loaded yet, still show the button so users can access tours menu
    if (import.meta.env.DEV) {
      logger.debug('getBaseAuthenticatedActions - tours button check', {
        showTours,
        systemEnabled: onboardingConfig?.enabled,
        allToursLength: allTours.length,
        hasIncompleteTours,
        incompleteTourCount,
        willAddToursButton: showTours && onboardingConfig?.enabled !== false,
      });
    }
    if (showTours && onboardingConfig?.enabled !== false) {
      const tourCount = allTours.length;
      const hasTours = tourCount > 0;
      const toursAction = {
        icon: hasTours && hasIncompleteTours ? <TourIcon /> : hasTours ? <CheckCircleIcon /> : <TourIcon />,
        name: hasTours 
          ? (hasIncompleteTours ? `Guided Tours (${incompleteTourCount})` : 'Guided Tours ✓')
          : 'Guided Tours',
        onClick: handleToursClick,
        color: hasTours && hasIncompleteTours ? theme.palette.warning.main : hasTours ? theme.palette.success.main : theme.palette.info.main,
        badge: hasTours && hasIncompleteTours ? incompleteTourCount : undefined,
        highlight: hasTours && hasIncompleteTours,
      };
      if (import.meta.env.DEV) {
        logger.debug('getBaseAuthenticatedActions - adding tours button', {
          actionName: toursAction.name,
          actionIndex: actions.length,
        });
      }
      actions.push(toursAction);
    }

    if (showBugReport) {
      actions.push({
        icon: <BugReportIcon />,
        name: 'Report Issue',
        onClick: handleBugReportClick,
        color: theme.palette.error.main,
      });
    }

    actions.push({
      icon: <HelpIcon />,
      name: 'Help/FAQ',
      onClick: handleHelpClick,
      color: theme.palette.info.main,
    });

    return actions;
  }, [showChat, showTours, showBugReport, allTours.length, hasIncompleteTours, incompleteTourCount, unreadCounts, onboardingConfig?.enabled, handleChatClick, handleMessagesClick, handleNotificationsClick, handleDashboardClick, handleFavoritesClick, handleToursClick, handleBugReportClick, handleHelpClick, theme, isLiveAgentOnline]);

  // Role-specific actions
  const getRoleSpecificActions = useCallback((): SpeedDialActionType[] => {
    const actions: SpeedDialActionType[] = [];

    // Admin actions
    if (isAdmin) {
      actions.push({
        icon: <AdminPanelSettingsIcon />,
        name: 'Backoffice',
        onClick: handleBackofficeClick,
        color: theme.palette.error.main,
        requiresRole: 'admin',
      });

      // Ad Blocking Diagnostics - admin only, dev mode only
      if (isDevMode) {
        actions.push({
          icon: <ScienceIcon />,
          name: 'Ad Blocking Diagnostics',
          onClick: handleAdBlockingDiagnosticsClick,
          color: theme.palette.warning.main,
          requiresRole: 'admin',
          devOnly: true,
        });
      }
    }

    // Coordinator actions
    // (All coordinator-specific actions removed per user request)
    if (isCoordinator) {
      // No coordinator-specific actions
    }

    // Training Manager actions
    // (All training manager-specific actions removed per user request)
    if (isTrainingManager) {
      // No training manager-specific actions
    }

    // Supervisor actions
    // (All supervisor-specific actions removed per user request)
    if (isSupervisor) {
      // No supervisor-specific actions
    }

    return actions;
  }, [isAdmin, isCoordinator, isTrainingManager, isSupervisor, isDevMode, theme, handleBackofficeClick, handleAdBlockingDiagnosticsClick]);

  // Build complete actions array based on authentication state and role
  // This must be defined AFTER all action generator functions
  const actions = useMemo(() => {
    if (!isAuthenticated || !user) {
      // Guest actions
      return getGuestActions();
    }

    // Authenticated user actions
    const baseActions = getBaseAuthenticatedActions();
    const roleActions = getRoleSpecificActions();

    // Filter role actions based on actual user roles and dev mode
    const filteredRoleActions = roleActions.filter(action => {
      // Filter dev-only actions
      if (action.devOnly && !isDevMode) {
        return false;
      }

      // Filter by required role
      if (action.requiresRole) {
        const requiredRoles = Array.isArray(action.requiresRole) 
          ? action.requiresRole 
          : [action.requiresRole];
        
        if (!hasAnyRole(user, requiredRoles)) {
          return false;
        }
      }

      // Filter by requiresAnyRole
      if (action.requiresAnyRole && !hasAnyRole(user, action.requiresAnyRole)) {
        return false;
      }

      return true;
    });

    const allActions = [...baseActions, ...filteredRoleActions];

    if (import.meta.env.DEV) {
      logger.debug('Speed dial actions built', {
        totalActions: allActions.length,
        isAuthenticated,
        primaryRole,
        baseActionsCount: baseActions.length,
        roleActionsCount: filteredRoleActions.length,
        unreadCounts,
        showTours,
        actionNames: allActions.map(a => a.name),
        hasToursButton: allActions.some(a => a.name.includes('Tours') || a.name.includes('Guided')),
      });
    }

    return allActions;
  }, [
    isAuthenticated,
    user,
    isDevMode,
    primaryRole,
    getGuestActions,
    getBaseAuthenticatedActions,
    getRoleSpecificActions,
    unreadCounts,
    showTours,
    showChat,
    showBugReport,
    onboardingConfig?.enabled,
    theme,
  ]);

  // Calculate highest priority badge count for main button
  // Only include counts for actions that are actually visible in the actions array
  // This must be calculated after actions array is built
  const mainButtonBadge = useMemo(() => {
    // Check which actions are actually visible
    const hasNotificationsAction = actions.some(a => a.name === 'Notifications' || a.name.includes('Notification'));
    const hasMessagesAction = actions.some(a => a.name === 'Messages' || a.name.includes('Message'));
    const hasChatAction = actions.some(a => a.name === 'Chat Support' || a.name.includes('Chat'));
    const hasToursAction = actions.some(a => a.name.includes('Tours') || a.name.includes('Guided'));
    
    // Priority: notifications > messages > chat > incomplete tours
    // Only show badge if the corresponding action is visible
    if (hasNotificationsAction && unreadCounts.notifications > 0) {
      return unreadCounts.notifications;
    }
    if (hasMessagesAction && unreadCounts.messages > 0) {
      return unreadCounts.messages;
    }
    if (hasChatAction && unreadCounts.chat > 0) {
      return unreadCounts.chat;
    }
    // Only include tours count if tours action is actually visible
    if (hasToursAction && hasIncompleteTours) {
      return incompleteTourCount;
    }
    return undefined;
  }, [actions, unreadCounts, hasIncompleteTours, incompleteTourCount]);

  const showLoadingBadge = countsLoading && isAuthenticated && actions.length > 0;

  const ariaLiveAnnouncement = useMemo(() => {
    if (!isAuthenticated) {
      return '';
    }
    if (countsLoading) {
      return 'Loading unread updates';
    }
    const parts: string[] = [];
    if (unreadCounts.notifications > 0) {
      parts.push(`${unreadCounts.notifications} notifications`);
    }
    if (unreadCounts.messages > 0) {
      parts.push(`${unreadCounts.messages} messages`);
    }
    if (unreadCounts.chat > 0) {
      parts.push(`${unreadCounts.chat} chat messages`);
    }
    if (parts.length === 0) {
      return 'No unread updates';
    }
    return `Unread: ${parts.join(', ')}`;
  }, [countsLoading, isAuthenticated, unreadCounts.chat, unreadCounts.messages, unreadCounts.notifications]);

  const srOnlyStyles = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  } as const;

  const getSpeedDialActionTestId = useCallback((name: string) => {
    if (name.includes('Guided Tours')) {
      return 'guided-tours';
    }
    if (name.includes('Chat Support') || name.includes('Live Chat')) {
      return 'chat-support';
    }
    if (name.includes('Report Issue')) {
      return 'report-issue';
    }
    if (name.includes('Notifications')) {
      return 'notifications';
    }
    if (name.includes('Messages')) {
      return 'messages';
    }
    if (name.includes('Dashboard')) {
      return 'dashboard';
    }
    if (name.includes('My Favorites')) {
      return 'favorites';
    }
    if (name.includes('Help/FAQ')) {
      return 'help-faq';
    }
    if (name.includes('Contact Us')) {
      return 'contact-us';
    }
    return name
      .toLowerCase()
      .replace(/\(.*\)/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, []);

  // Debug: Log when actions change
  useEffect(() => {
    if (import.meta.env.DEV) {
      logger.debug('Actions array changed', {
        totalActions: actions.length,
        actionNames: actions.map(a => a.name),
        showTours,
        hasToursButton: actions.some(a => a.name.includes('Tours') || a.name.includes('Guided')),
      });
    }
  }, [actions, showTours]);

  // Keyboard navigation: Handle Escape key to close speed dial
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [open, handleClose]);

  // Don't render if no actions available or should be hidden (K9/backoffice subdomains)
  if (actions.length === 0 || shouldHideSpeedDial) {
    return null;
  }

  return (
    <>
      {/* Backdrop when speed dial is open - enterprise-grade subtle backdrop.
          pointerEvents must track `open`: MUI Fade keeps visibility:visible while
          opacity animates to 0, and an opacity-0 overlay still eats clicks — that
          forces a double-click on dashboard role tabs / other controls underneath. */}
      <Backdrop
        open={open}
        onClick={handleClose}
        sx={{
          zIndex: (theme) => theme.zIndex.speedDial - 1,
          pointerEvents: open ? 'auto' : 'none',
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.black, 0.5)
            : alpha(theme.palette.common.black, 0.4),
          backdropFilter: 'blur(6px)',
          transition: theme.transitions.create(['opacity', 'backdrop-filter'], {
            duration: theme.transitions.duration.short,
            easing: theme.transitions.easing.easeOut,
          }),
        }}
      />

      <Box aria-live="polite" sx={srOnlyStyles}>
        {ariaLiveAnnouncement}
      </Box>

      <Box ref={speedDialRootRef} sx={{ display: 'contents' }}>
      <SpeedDial
        ariaLabel={ariaLabel}
        data-testid="help-speed-dial"
        data-tour-stable="help-speed-dial"
        sx={{
          position: 'fixed',
          // Material Design FAB: 16dp mobile / 24dp desktop + safe-area;
          // --apt-bottom-chrome-height clears sticky bottom bars when present.
          bottom: fabBottomCss(isMobile, isDashboardMobile ? 8 : 0),
          right: fabRightCss(isMobile),
          zIndex: (theme) => theme.zIndex.speedDial,
          '& .MuiFab-primary': {
            // Enterprise-grade FAB - polished and professional
            width: fabSizePx,
            height: fabSizePx,
            minHeight: fabSizePx,
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.grey[800] 
              : theme.palette.primary.main,
            color: theme.palette.common.white,
            // Enterprise shadow matching system design
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
              : `1px solid ${alpha(theme.palette.primary.dark, 0.15)}`,
            borderRadius: '50%',
            // Smooth, professional transitions
            transition: theme.transitions.create(
              ['background-color', 'box-shadow', 'transform', 'border-color'],
              {
                duration: theme.transitions.duration.short,
                easing: theme.transitions.easing.easeOut,
              }
            ),
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? theme.palette.grey[700]
                : theme.palette.primary.dark,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 20px rgba(0, 0, 0, 0.5), 0 4px 10px rgba(0, 0, 0, 0.4)'
                : '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 10px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px) scale(1.02)',
              borderColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.primary.dark, 0.25),
            },
            '&:active': {
              transform: 'translateY(0) scale(0.98)',
            },
          },
          '& .MuiSpeedDial-actions': {
            alignItems: 'center',
          },
        }}
        icon={
          <Badge
            badgeContent={showLoadingBadge ? undefined : (mainButtonBadge ?? (isLiveAgentOnline ? 'Live' : undefined))}
            color={showLoadingBadge ? 'info' : mainButtonBadge ? 'error' : 'success'}
            variant={showLoadingBadge ? 'dot' : 'standard'}
            overlap="circular"
            invisible={!showLoadingBadge && !mainButtonBadge && !isLiveAgentOnline}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: showLoadingBadge
                  ? theme.palette.info.main
                  : mainButtonBadge
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                color: showLoadingBadge
                  ? theme.palette.info.contrastText
                  : mainButtonBadge
                    ? theme.palette.error.contrastText
                    : theme.palette.success.contrastText,
                fontSize: showLoadingBadge || mainButtonBadge ? '0.875rem' : '0.65rem',
                fontWeight: theme.typography.fontWeightBold,
                minWidth: showLoadingBadge ? 10 : mainButtonBadge ? 24 : 28,
                height: showLoadingBadge ? 10 : mainButtonBadge ? 24 : 18,
                px: !showLoadingBadge && !mainButtonBadge && isLiveAgentOnline ? 0.75 : undefined,
                border: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.primary.main}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 6px rgba(0, 0, 0, 0.4)'
                  : '0 2px 6px rgba(244, 67, 54, 0.3)',
                top: -12,
                right: -12,
                animation: 'none',
              },
            }}
          >
            <SpeedDialIcon
              icon={<PlayIcon sx={{ fontSize: 24, transition: 'transform 0.2s ease' }} />}
              openIcon={<CloseIcon sx={{ fontSize: 24, transition: 'transform 0.2s ease' }} />}
            />
          </Badge>
        }
        onOpen={(event, reason) => {
          // Open on explicit click (toggle) only — not on focus. A mouse click fires focus
          // then toggle; opening on both opens and immediately closes in one gesture.
          // Keyboard users still open via Enter/Space on the FAB (toggle).
          if (reason !== 'toggle') {
            return;
          }
          handleOpen();
        }}
        onClose={(event, reason) => {
          // Only close on explicit actions, not on mouse leave
          // MUI SpeedDial reasons: 'toggle', 'escapeKeyDown', 'backdropClick', 'mouseLeave'
          if (reason === 'toggle' || reason === 'escapeKeyDown' || reason === 'backdropClick') {
            handleClose();
          }
          // Ignore 'mouseLeave' to prevent hover-based closing
        }}
        open={open}
        direction="up"
        ref={speedDialButtonRef}
      >
        {actions.map((action) => {
          const position = (action as { position?: { x: number; y: number } }).position;
          const badgeColor = action.badgeColor || 'error';
          const shouldShowBadge = action.badge !== undefined && action.badge > 0;
          const actionTestId = getSpeedDialActionTestId(action.name);
          
          const hasCustomPosition = Boolean(position);
          return (
            <SpeedDialAction
              key={action.name}
              data-testid={`speed-dial-action-${actionTestId}`}
              tooltipOpen
              tooltipPlacement="left"
              icon={
                shouldShowBadge ? (
                  <Badge
                    badgeContent={action.badge}
                    color={badgeColor}
                    overlap="circular"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: badgeColor === 'error' 
                          ? '#d32f2f' 
                          : badgeColor === 'primary'
                          ? theme.palette.primary.main
                          : undefined,
                        color: 'white',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px',
                        border: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'white'}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        top: -4,
                        right: -4,
                      },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.375rem', // 22px icon for 44px button
                        margin: 0,
                      },
                    }}
                  >
                    {action.icon}
                  </Badge>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.375rem', // 22px icon for 44px button
                        margin: 0,
                      },
                    }}
                  >
                    {action.icon}
                  </Box>
                )
              }
              tooltipTitle={
                <Box sx={{ textAlign: 'center', py: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      letterSpacing: '0.01em',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {action.name}
                  </Typography>
                </Box>
              }
              onClick={action.onClick}
              aria-label={action.name}
              sx={{
                // Apply calculated positioning directly to this action (single column or 2-column layout)
                // Override MUI's default direction-based transform
                transform: hasCustomPosition && open
                  ? `translate(${position!.x.toFixed(2)}px, ${position!.y.toFixed(2)}px)`
                  : undefined,
                position: hasCustomPosition ? 'absolute' : undefined,
                margin: hasCustomPosition ? 0 : undefined,
                // Transition for smooth animation when custom positioned
                transition: hasCustomPosition
                  ? theme.transitions.create(['transform'], {
                      duration: theme.transitions.duration.short,
                      easing: theme.transitions.easing.easeOut,
                    })
                  : undefined,
                '& .MuiSpeedDialAction-fab': {
                  backgroundColor: action.color,
                  color: theme.palette.common.white,
                  // Enterprise-grade action button sizing
                  width: 44,
                  height: 44,
                  minHeight: 44,
                  // Professional shadows matching enterprise design system
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)'
                    : `0 4px 12px ${alpha(action.color, 0.3)}, 0 2px 6px ${alpha(action.color, 0.2)}`,
                  border: action.highlight 
                    ? `2px solid ${alpha(theme.palette.common.white, 0.4)}`
                    : `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                  borderRadius: '50%',
                  // Center icon properly using flexbox
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Ensure icons are properly sized and centered
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.375rem', // 22px icon for 44px button
                    margin: 0,
                  },
                  // Center Badge content if present
                  '& .MuiBadge-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.375rem',
                      margin: 0,
                    },
                  },
                  // Enterprise hover effects
                  '&:hover': {
                    backgroundColor: alpha(action.color, 0.85),
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 6px 16px rgba(0, 0, 0, 0.5), 0 3px 8px rgba(0, 0, 0, 0.4)'
                      : `0 6px 16px ${alpha(action.color, 0.4)}, 0 3px 8px ${alpha(action.color, 0.3)}`,
                    transform: 'translateY(-2px) scale(1.05)',
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.95)',
                  },
                  transition: theme.transitions.create(
                    ['background-color', 'box-shadow', 'transform', 'border-color'],
                    {
                      duration: theme.transitions.duration.short,
                      easing: theme.transitions.easing.easeOut,
                    }
                  ),
                },
                '& .MuiSpeedDialAction-staticTooltipLabel': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.grey[900], 0.95)
                    : alpha(theme.palette.grey[900], 0.92),
                  color: theme.palette.common.white,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 24px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.4)'
                    : '0 8px 24px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: theme.shape.borderRadius || 8,
                  whiteSpace: 'nowrap',
                  padding: theme.spacing(0.75, 1.25),
                  fontSize: '0.8125rem', // 13px - slightly larger for readability
                  fontWeight: theme.typography.fontWeightMedium || 500,
                  letterSpacing: '0.01em',
                  backdropFilter: 'blur(12px)',
                  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                  // Smooth tooltip transitions
                  transition: theme.transitions.create(['opacity', 'transform'], {
                    duration: theme.transitions.duration.short,
                  }),
                },
              }}
            />
          );
        })}
      </SpeedDial>
      </Box>

      <LiveAgentAvailabilityCallout
        isLive={isLiveAgentOnline}
        suppressed={open}
        fabExtraBottomPx={isDashboardMobile ? 8 : 0}
        onChatNow={handleChatClick}
      />

      {/* Onboarding Tours Menu */}
      <OnboardingMenu
        anchorEl={tourMenuAnchorEl}
        open={Boolean(tourMenuAnchorEl)}
        onClose={handleTourMenuClose}
      />

      {/* Ad Blocking Diagnostics - Admin only, Dev mode only */}
      {isAdmin && isDevMode && showAdBlockingDiagnostics && typeof document !== 'undefined' 
        ? createPortal(<AdBlockingDiagnostics />, document.body)
        : null}
    </>
  );
};

export default UnifiedHelpSpeedDial;

